import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { Button, Container, Text, Form, Spacer, IconStack, Loader, Icon } from 'roadie-ui'
import Page from 'rui/widgets/Page'
import DriveSidebar from 'components/layout/DriveSidebar'
import Panel from 'components/layout/Panel'
import { NewVehicleForm, getVehicleCategoryIcon } from '../Vehicles'
import { VehicleFormStates } from 'util/vehicle'
import { ProfileVehicle } from 'types/profile'
import { Breadcrumb } from 'components/misc/Breadcrumb'
import ButtonLink from 'components/Button/ButtonLink'
import { Redirect } from 'react-router-dom'
import { FlaggedInsuranceSubmissionModal } from '../VehicleInfo/InsuranceSubmission'
import { ModalOptions } from 'types/global'
import { useInsurancePollingContext } from 'context/InsurancePollingContext'
import { useHandleCancelVehicle } from './hooks/useHandleCancelVehicle'
import { useAddNewVehicleFormViewModel } from './hooks/useAddNewVehicleFormViewModel'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

export const SearchingForVehicle: React.FC = () => {
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)

  return (
    <Container.Grid
      $height={isWebView ? '100vh' : '30vh'}
      $padding={isWebView ? '4rem' : '0rem'}
      $display="flex"
      $alignItems="center"
      $justifyContent="center"
    >
      <Container.Base>
        <Loader />
        <Spacer $height="3.6rem" />
        <Text.H4 $color="neutral-black">
          <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_searching_vehicle" />
        </Text.H4>
      </Container.Base>
    </Container.Grid>
  )
}

export const VehicleAddedConfirmation: React.FC<{
  vehicle: ProfileVehicle | null
  vehicleId?: number
  redirectPath: string
  handleAcceptDecodedPlateVIN?: () => void
  setFormState: React.Dispatch<React.SetStateAction<VehicleFormStates | null>>
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
}> = (props) => {
  const { status, response } = useInsurancePollingContext()
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const handleCancel = useHandleCancelVehicle({
    vehicleId: props.vehicleId,
    newVehicleFlow: true,
    setFormState: props.setFormState,
  })

  if (props.vehicleId === undefined) {
    return <Redirect to="/account/driving-info" />
  }

  if (status === 'polling') {
    return <SearchingForVehicle />
  }

  if (status === 'accepted') {
    return <Redirect to={`vehicle-info/${props.vehicleId}`} />
  }

  // Redirect when rejected with plate_vin_mismatch
  if (status === 'rejected' && response?.plate_vin_mismatch) {
    return <Redirect to={`vehicle-info/${props.vehicleId}`} />
  }

  return (
    <Container.Grid $height={isWebView ? '100vh' : 'auto'} $alignItems="center" $justifyItems="center">
      <Container.Grid $alignItems="center" $justifyItems="center">
        <Container.Base $width="7rem" $height="7rem">
          <IconStack>
            <Container.Base $borderRadius="100%" $backgroundColor="primary-lighter" $height="100%" />
            <Container.Base $color="primary-dark" $padding=".75rem">
              {getVehicleCategoryIcon(
                props.vehicle?.specs.category,
                props.vehicle?.specs.model,
                props.vehicle?.details?.vin ?? null
              )}
            </Container.Base>
          </IconStack>
        </Container.Base>
        <Spacer $height="2.4rem" />
        <Container.Grid>
          <Text.H4 $fontWeight="700" $color="neutral-black" $textAlign="center">
            <LocalizedRichText
              translationKey="dweb_account_driving_info_new_vehicle_add_value1_profile"
              params={{ value1: props.vehicle?.plate_lookup.display_name }}
            />
          </Text.H4>
          <Spacer $height="2.4rem" />
          <Text.Caption $textAlign="center">
            <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_vehicle_we_found_from_info_provided_if_not" />
          </Text.Caption>
        </Container.Grid>
      </Container.Grid>
      <Spacer $height="2.4rem" />
      <Container.Base $width="100%">
        <Button.Primary $width="100%" onClick={props.handleAcceptDecodedPlateVIN} data-cy="confirm-add-vehicle-button">
          <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_yes_add_vehicle" />
        </Button.Primary>
        <Spacer $height="2.4rem" />
        <Button.Secondary
          $width="100%"
          onClick={() => {
            props.setModalOptions?.({ modalOpen: true, modalType: 'add-insurance' })
          }}
          data-cy="add-proof-of-insurance-button"
        >
          <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_no_add_proof_insurance" />
        </Button.Secondary>
        <Spacer $height="2.4rem" />
        <Button.Tertiary $width="100%" onClick={handleCancel} data-cy="confirmation-cancel-button">
          <LocalizedText translationKey="dweb_common_cancel" />
        </Button.Tertiary>
      </Container.Base>
      <FlaggedInsuranceSubmissionModal
        modalOptions={props.modalOptions}
        setModalOptions={props.setModalOptions}
        vehicle_id={props.vehicleId}
        fromFailureModal={false}
        openModalOnTryAgain={false}
      />
    </Container.Grid>
  )
}

export const BumperServerError: React.FC<{
  handleClose?: () => void
}> = ({ handleClose }) => {
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)

  return (
    <Container.Grid $height={isWebView ? '100vh' : '30vh'} $alignItems="center" $justifyItems="center">
      <Container.Grid $alignItems="center" $justifyItems="center">
        <Icon.AlertFilled $color="alert-light" height="7rem" />
        <Spacer $height="2.4rem" />
        <Text.H4 $fontWeight="700" $color="neutral-black" $textAlign="center">
          <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_issue_connecting" />
        </Text.H4>
        <Spacer $height="2.4rem" />
        <Text.Caption>
          <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_we_ran_into_issue_connecting_server_please_try" />
        </Text.Caption>
      </Container.Grid>
      <Button.Primary onClick={handleClose} $width="100%" data-cy="server-error-close-button">
        <LocalizedText translationKey="dweb_common_close" />
      </Button.Primary>
    </Container.Grid>
  )
}

export const UnsupportedTypeError: React.FC<{
  vehicle_id?: number
  newVehicleFlow: boolean
  setFormState: React.Dispatch<React.SetStateAction<VehicleFormStates | null>>
}> = ({ vehicle_id, newVehicleFlow, setFormState }) => {
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const handleCancel = useHandleCancelVehicle({ vehicleId: vehicle_id, newVehicleFlow, setFormState })

  return (
    <Container.Grid $height={isWebView ? '100vh' : '30vh'} $alignItems="center" $justifyItems="center">
      <Container.Grid $alignItems="center" $justifyItems="center">
        <Icon.AlertFilled $color="error-light" height="7rem" />
        <Spacer $height="2.4rem" />
        <Text.H4 $fontWeight="700" $color="neutral-black" $textAlign="center">
          <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_vehicle_type_not_supported" />
        </Text.H4>
        <Spacer $height="2.4rem" />
        <Text.Caption>
          <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_vehicle_type_not_supported_description" />
        </Text.Caption>
      </Container.Grid>
      <Container.Base $width="100%">
        <Button.Primary
          $width="100%"
          onClick={() => setFormState('retryLicensePlate')}
          data-cy="retry-license-plate-button"
        >
          <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_retry_license_plate" />
        </Button.Primary>
        <Spacer $height="2.4rem" />
        <Button.Tertiary $width="100%" onClick={handleCancel} data-cy="unsupported-cancel-button">
          <LocalizedText translationKey="dweb_common_cancel" />
        </Button.Tertiary>
      </Container.Base>
    </Container.Grid>
  )
}

export const LicenseSearchFailure: React.FC<{
  vehicle_id?: number
  newVehicleFlow: boolean
  license_plate: string | null
  setFormState: React.Dispatch<React.SetStateAction<VehicleFormStates | null>>
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
}> = ({ vehicle_id, newVehicleFlow, license_plate, setFormState, modalOptions, setModalOptions }) => {
  const { status } = useInsurancePollingContext()
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const handleCancel = useHandleCancelVehicle({ vehicleId: vehicle_id, newVehicleFlow, setFormState })

  // Polling for insurance submission status
  if (status === 'polling') {
    return <SearchingForVehicle />
  }

  // Successfully added vehicle after insurance submission
  if (status === 'accepted') {
    return <Redirect to={`vehicle-info/${vehicle_id}`} />
  }

  return (
    <Container.Grid $height={isWebView ? '100vh' : '100%'} $justifyItems="center" $alignItems="center">
      <Container.Grid $alignItems="center" $justifyItems="center">
        <Container.Grid $width="7rem" $height="7rem">
          <IconStack>
            <Icon.AlertFilled $color="error-light" />
          </IconStack>
        </Container.Grid>
        <Spacer $height="2.4rem" />
        <Text.H4 $fontWeight="700" $color="neutral-black" $textAlign="center">
          <LocalizedRichText
            translationKey="dweb_account_driving_info_new_vehicle_no_match_found_value1"
            params={{ value1: license_plate }}
          />
        </Text.H4>
        <Spacer $height="2.4rem" />
        <Text.Body1 $color="neutral-darker" $fontSize="18px">
          <LocalizedRichText
            translationKey="dweb_account_driving_info_new_vehicle_no_match_next_steps"
            params={{
              strongText1: (
                <strong>
                  <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_retry_license_plate_lowercase" />
                </strong>
              ),
              br: <br />,
              strongText2: (
                <strong>
                  <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_add_proof_insurance_lowercase" />
                </strong>
              ),
            }}
          />
        </Text.Body1>
      </Container.Grid>
      <Spacer $height="6rem" />
      <Container.Base $width="100%">
        <Button.Primary
          $width="100%"
          onClick={() => setFormState('retryLicensePlate')}
          data-cy="search-failure-retry-button"
        >
          <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_retry_license_plate" />
        </Button.Primary>
        <Spacer $height="2.4rem" />
        {newVehicleFlow && (
          <>
            <Button.Secondary
              $width="100%"
              onClick={() => {
                setModalOptions?.({ modalOpen: true, modalType: 'add-insurance' })
              }}
              data-cy="search-failure-add-insurance-button"
            >
              <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_add_proof_insurance" />
            </Button.Secondary>
            <Spacer $height="2.4rem" />
          </>
        )}

        <Button.Tertiary $width="100%" onClick={handleCancel} data-cy="search-failure-cancel-button">
          <LocalizedText translationKey="dweb_common_cancel" />
        </Button.Tertiary>
      </Container.Base>
      <FlaggedInsuranceSubmissionModal
        modalOptions={modalOptions}
        setModalOptions={setModalOptions}
        vehicle_id={vehicle_id ?? 0}
        fromFailureModal={false}
        openModalOnTryAgain={false}
      />
    </Container.Grid>
  )
}

const AddNewVehicleForm: React.FC = () => {
  const {
    formState,
    redirectPath,
    createVehicleResp,
    newVehicle,
    formData,
    formDataChanged,
    modalOptions,
    setFormState,
    setFormDataChanged,
    setModalOptions,
    handleData,
    handleRetry,
    handleAcceptDecodedPlateVIN,
    handleCancel,
  } = useAddNewVehicleFormViewModel()

  // Loading screen while vehicles is looking up license plate
  if (formState === 'searchingVehicle') {
    return <SearchingForVehicle />
  }

  // Confirmation of new vehicle added
  if (formState === 'vehicleFound') {
    return (
      <VehicleAddedConfirmation
        vehicle={newVehicle}
        vehicleId={createVehicleResp?.id}
        redirectPath={redirectPath}
        handleAcceptDecodedPlateVIN={handleAcceptDecodedPlateVIN}
        setFormState={setFormState}
        modalOptions={modalOptions}
        setModalOptions={setModalOptions}
      />
    )
  }

  // Bumper server error
  if (formState === 'serverError') {
    return <BumperServerError handleClose={handleCancel} />
  }

  // Unsupported vehicle type error
  if (formState === 'unsupportedType') {
    return <UnsupportedTypeError vehicle_id={createVehicleResp?.id} newVehicleFlow={true} setFormState={setFormState} />
  }

  // License plate search failed
  if (formState === 'vehicleNotFound') {
    return (
      <LicenseSearchFailure
        license_plate={formData.license_plate}
        newVehicleFlow={true}
        vehicle_id={createVehicleResp?.id}
        setFormState={setFormState}
        modalOptions={modalOptions}
        setModalOptions={setModalOptions}
      />
    )
  }

  // Retry license plate
  if (formState === 'retryLicensePlate') {
    return (
      <Container.Base $padding="2rem 0rem">
        <Form.Basic
          id="new-vehicle-retry-form"
          onSubmit={handleRetry}
          validations={[
            ['color', Form.validations.required],
            ['license_plate', Form.validations.required],
            ['license_state', Form.validations.required],
          ]}
        >
          <Text.H4 $color="neutral-black">
            <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_add_new_vehicle" />
          </Text.H4>
          <NewVehicleForm
            registration={false}
            defaultValues={formData}
            errorState="License plate not found"
            setFormDataChanged={setFormDataChanged}
          />
          <Spacer $height="2.4rem" />
          <Container.Grid>
            <Container.GridItem $justifySelf="center" $width="100%" $maxWidth="40rem">
              <Form.Submit disabled={!formDataChanged} data-cy="retry-save-button">
                <LocalizedText translationKey="dweb_common_save" />
              </Form.Submit>
            </Container.GridItem>
            <Spacer $height="2.4rem" />
            <Container.GridItem $justifySelf="center" $width="100%" $maxWidth="40rem">
              <Button.Secondary
                $borderWidth="0"
                $width="100%"
                type="button"
                id="cancel-add-new-vehicle"
                onClick={handleCancel}
                data-cy="retry-cancel-button"
              >
                <LocalizedText translationKey="dweb_common_cancel" />
              </Button.Secondary>
            </Container.GridItem>
          </Container.Grid>
        </Form.Basic>
      </Container.Base>
    )
  }

  // Default is Add New Vehicle form
  return (
    <Container.Base $padding="2rem 0rem">
      <Form.Basic
        id="new-vehicle-form"
        onSubmit={handleData}
        validations={[
          ['color', Form.validations.required],
          ['license_plate', Form.validations.required],
          ['license_state', Form.validations.required],
        ]}
      >
        <Text.H4 $color="neutral-black">
          <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_add_new_vehicle" />
        </Text.H4>
        <NewVehicleForm registration={false} />
        <Spacer $height="2.4rem" />
        <Container.Grid>
          <Container.GridItem $justifySelf="center" $width="100%" $maxWidth="40rem">
            <Form.Submit data-cy="save-button">
              <LocalizedText translationKey="dweb_common_save" />
            </Form.Submit>
          </Container.GridItem>
          <Spacer $height="2.4rem" />
          <Container.GridItem $justifySelf="center" $width="100%" $maxWidth="40rem">
            <ButtonLink
              variant="secondary"
              $borderWidth="0"
              $width="100%"
              type="button"
              id="driving-info-link"
              to={'/account/driving-info'}
              data-cy="cancel-button"
            >
              <LocalizedText translationKey="dweb_common_cancel" />
            </ButtonLink>
          </Container.GridItem>
        </Container.Grid>
      </Form.Basic>
    </Container.Base>
  )
}

const NewVehiclePage: React.FC = () => {
  const { t } = useLocalization()
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_account_driving_info_new_vehicle_add_new_vehicle')}</title>
      </Helmet>
      {!isWebView && (
        <Breadcrumb
          items={[
            { label: t('dweb_common_driving_info'), linkTo: '/account/driving-info' },
            { label: t('dweb_account_driving_info_new_vehicle_add_new_vehicle') },
          ]}
        />
      )}
      <Panel>
        <AddNewVehicleForm />
      </Panel>
    </Page>
  )
}

export default NewVehiclePage
